import axios from "../lib/axios";
import { renewToken } from "./auth";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import {
  SEATS_ADDON_KEY,
  seatsCheckoutRequest,
  seatsCompleteCheckoutRequest,
  seatsPreviewRequest,
} from "../utils/extraSeats";
import { LaunchError } from "../utils/errors";
import { buildFeatureAddonLaunchErrorArgs } from "../utils/whiteLabelAddon";

export const FEATURE_ADDON_KEYS = Object.freeze({
  AUTOMATIONS: "automations",
  TEMPLATES: "templates",
  COMPETITORS: "competitors",
  WHITE_LABEL: "whiteLabel",
  SEATS: SEATS_ADDON_KEY,
});

const reportFeatureAddonError = ({
  dispatch,
  error,
  functionName,
  showAlert,
}) => {
  dispatch(
    LaunchError(
      error,
      ...buildFeatureAddonLaunchErrorArgs({ functionName, showAlert })
    )
  );
};

const postFeatureAddonFunction = async (dispatch, functionName, data = {}) => {
  const token = await dispatch(renewToken());
  try {
    const response = await axios({
      method: "POST",
      url: `${getCloudFunctionUrl(functionName)}/${functionName}`,
      data,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    const body = error?.response?.data;
    if (body && typeof body === "object") {
      return body;
    }
    throw error;
  }
};

export const startPreviewFeatureAddonProration =
  ({ addonKey }) =>
  async (dispatch) => {
    try {
      return await postFeatureAddonFunction(
        dispatch,
        "previewFeatureAddonProration",
        { addonKey }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startPreviewFeatureAddonProration",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startCreateFeatureAddonCheckout =
  ({ addonKey, tierId, origin }) =>
  async (dispatch) => {
    try {
      return await postFeatureAddonFunction(
        dispatch,
        "createFeatureAddonCheckout",
        { addonKey, tierId, origin }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreateFeatureAddonCheckout",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startCancelFeatureAddon =
  ({ addonKey }) =>
  async (dispatch) => {
    try {
      return await postFeatureAddonFunction(dispatch, "cancelFeatureAddon", {
        addonKey,
      });
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCancelFeatureAddon",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startReactivateFeatureAddon =
  ({ addonKey }) =>
  async (dispatch) => {
    try {
      return await postFeatureAddonFunction(
        dispatch,
        "reactivateFeatureAddon",
        {
          addonKey,
        }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startReactivateFeatureAddon",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startUpdateFeatureAddonPaymentMethod =
  ({ addonKey, origin }) =>
  async (dispatch) => {
    try {
      return await postFeatureAddonFunction(
        dispatch,
        "updateFeatureAddonPaymentMethod",
        { addonKey, origin }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUpdateFeatureAddonPaymentMethod",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startCompleteFeatureAddonPaymentMethod =
  ({ addonKey, sessionId }) =>
  async (dispatch) => {
    try {
      return await postFeatureAddonFunction(
        dispatch,
        "completeFeatureAddonPaymentMethod",
        { addonKey, sessionId }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCompleteFeatureAddonPaymentMethod",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startSubmitWhiteLabelBranding = (branding) => async (dispatch) => {
  try {
    return await postFeatureAddonFunction(
      dispatch,
      "submitWhiteLabelBranding",
      branding
    );
  } catch (error) {
    reportFeatureAddonError({
      dispatch,
      error,
      functionName: "startSubmitWhiteLabelBranding",
      showAlert: true,
    });
    return { ok: false };
  }
};

export const startSetWhiteLabelSetupStatus =
  ({ agencyID, status }) =>
  async (dispatch) => {
    try {
      return await postFeatureAddonFunction(
        dispatch,
        "setWhiteLabelSetupStatus",
        { agencyID, status }
      );
    } catch (error) {
      reportFeatureAddonError({
        dispatch,
        error,
        functionName: "startSetWhiteLabelSetupStatus",
        showAlert: true,
      });
      return { ok: false };
    }
  };

export const startSaveCompetitorFavoriteMetered =
  ({ pageId, libraryID, favorite }) =>
  async (dispatch) => {
    try {
      return await postFeatureAddonFunction(
        dispatch,
        "saveCompetitorFavorite",
        { pageId, libraryID, favorite }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveCompetitorFavoriteMetered",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startGenerateCompetitorMyVersionMetered =
  ({ pageId, libraryID }) =>
  async (dispatch) => {
    try {
      return await postFeatureAddonFunction(
        dispatch,
        "generateCompetitorMyVersion",
        { pageId, libraryID }
      );
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGenerateCompetitorMyVersionMetered",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startPreviewSeatsChange =
  ({ targetQuantity }) =>
  async (dispatch) => {
    try {
      const { functionName, data } = seatsPreviewRequest({ targetQuantity });
      return await postFeatureAddonFunction(dispatch, functionName, data);
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startPreviewSeatsChange",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startChangeSeats =
  ({ targetQuantity, expectedQuantity, requestID, origin, returnTo }) =>
  async (dispatch) => {
    try {
      const { functionName, data } = seatsCheckoutRequest({
        targetQuantity,
        expectedQuantity,
        requestID,
        origin,
        returnTo,
      });
      return await postFeatureAddonFunction(dispatch, functionName, data);
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startChangeSeats",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startConfirmSeatsCheckout =
  ({ sessionID }) =>
  async (dispatch) => {
    try {
      const { functionName, data } = seatsCompleteCheckoutRequest({
        sessionID,
      });
      return await postFeatureAddonFunction(dispatch, functionName, data);
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startConfirmSeatsCheckout",
          tags: { action: "featureAddons" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const isFeatureAddonLimitError = (result) =>
  Boolean(
    result &&
      result.ok === false &&
      (result.code === "featureAddonCapacityLimit" ||
        result.code === "featureAddonPeriodicLimit")
  );

export const periodicAddonUsageCount = (agencyAddon, counterKey) => {
  const usage = agencyAddon?.usage;
  if (!usage) return 0;
  return Number(usage[counterKey]) || 0;
};

export const isAddonUsageAtLimit = (status, used) => {
  if (!status?.ok || status.ungated || status.eligible === false) return false;
  const limit = Number(status?.effective?.limit);
  if (!Number.isFinite(limit)) return false;
  return Number(used) >= limit;
};
